import {
	Accordion,
	ActionButton,
	Button,
	ButtonGroup,
	Disclosure,
	DisclosurePanel,
	DisclosureTitle,
	Menu,
	MenuItem,
	MenuTrigger,
	Popover,
	ProgressCircle,
	Text,
	TextArea,
	TextField,
} from '@react-spectrum/s2';
import Add from '@react-spectrum/s2/icons/Add';
import ChevronDown from '@react-spectrum/s2/icons/ChevronDown';
import ChevronUp from '@react-spectrum/s2/icons/ChevronUp';
import Close from '@react-spectrum/s2/icons/Close';
import GlobeGrid from '@react-spectrum/s2/icons/GlobeGrid';
import Location from '@react-spectrum/s2/icons/Location';
import MagicWand from '@react-spectrum/s2/icons/MagicWand';
import More from '@react-spectrum/s2/icons/More';
import {
	iconStyle,
	style,
} from '@react-spectrum/s2/style' with { type: 'macro' };
import { memo, useCallback, useEffect, useRef, useState } from 'react';

import { EditableTagList } from '@/components/EditableTagList';
import { FacebookLogo } from '@/components/icons/socials-monochrome/FacebookLogo';
import { InstagramLogo } from '@/components/icons/socials-monochrome/InstagramLogo';
import { LinkedInLogo } from '@/components/icons/socials-monochrome/LinkedInLogo';
import { RedditLogo } from '@/components/icons/socials-monochrome/RedditLogo';
import { TikTokLogo } from '@/components/icons/socials-monochrome/TikTokLogo';
import { XLogo } from '@/components/icons/socials-monochrome/XLogo';
import { YouTubeLogo } from '@/components/icons/socials-monochrome/YouTubeLogo';
import { InfoBanner } from '@/components/InfoBanner';
import { useBlobAccessUrlWithStatus } from '@/hooks/useBlobAccessUrl';
import { useEditableTagList } from '@/hooks/useEditableTagList';
import { cn } from '@/utils/cn';

import type {
	EditableCompetitorArrayField,
	EditableCompetitorField,
	SocialPlatformKey,
} from '../../../../bridge/types/competitor-fields.ts';
import type {
	Competitor,
	CompetitorProduct,
} from '../../../../bridge/types/competitors.ts';

function asDisplayString(v: unknown): string | null {
	return typeof v === 'string' && v.length > 0 ? v : null;
}

function hashHue(name: string): number {
	let hash = 0;
	for (let i = 0; i < name.length; i++) {
		hash = name.charCodeAt(i) + ((hash << 5) - hash);
	}
	return Math.abs(hash) % 360;
}

function handleFromUrl(url: string) {
	const path = (url ?? '').replace(/\/+$/, '').split('/').pop() ?? url;
	return path.startsWith('@') ? path : `@${path}`;
}

const SOCIAL_PLATFORMS = [
	{ key: 'instagram', Icon: InstagramLogo, followerField: 'followers' },
	{ key: 'facebook', Icon: FacebookLogo, followerField: 'followers' },
	{ key: 'x', Icon: XLogo, followerField: 'followers' },
	{ key: 'linkedin', Icon: LinkedInLogo, followerField: 'followers' },
	{ key: 'tiktok', Icon: TikTokLogo, followerField: 'followers' },
	{ key: 'youtube', Icon: YouTubeLogo, followerField: 'subscribers' },
	{ key: 'reddit', Icon: RedditLogo, followerField: 'members' },
] as const;

function SectionLabel({ children }: { children: React.ReactNode }) {
	return (
		<Text
			styles={style({
				font: 'detail',
				fontSize: 'detail',
				fontWeight: 'bold',
				color: 'detail',
				textTransform: 'uppercase',
			})}
			UNSAFE_style={{ letterSpacing: '0.08em' }}
		>
			{children}
		</Text>
	);
}

function SocialLink({
	url,
	icon,
	label,
	followers,
}: {
	url: string;
	icon: React.ReactNode;
	label: string;
	followers?: number;
}) {
	return (
		<div className="flex items-center gap-2">
			<a
				href={url}
				target="_blank"
				rel="noopener noreferrer"
				className="flex items-center gap-2 text-blue-600 hover:underline"
			>
				{icon}
				<span className="text-sm">{label}</span>
			</a>
			{typeof followers === 'number' && Number.isFinite(followers) && (
				<Text
					styles={style({
						font: 'body',
						fontSize: 'body-sm',
						color: 'neutral',
					})}
				>
					({followers.toLocaleString()} followers)
				</Text>
			)}
		</div>
	);
}

function BulletList({ title, items }: { title: string; items: string[] }) {
	if (items.length === 0) return null;
	return (
		<div className="flex flex-col gap-2">
			<SectionLabel>{title}</SectionLabel>
			<ul className="flex list-disc flex-col gap-1.5 pl-4 text-sm text-[#333]">
				{items.map((item) => (
					<li key={item}>{item}</li>
				))}
			</ul>
		</div>
	);
}

function StrengthsWeaknesses({
	strengths,
	weaknesses,
}: {
	strengths: string[];
	weaknesses: string[];
}) {
	if (strengths.length === 0 && weaknesses.length === 0) return null;
	return (
		<div className="grid grid-cols-2 rounded-xl bg-[#f8f8f8]">
			<div className="p-4">
				<BulletList title="Strengths" items={strengths} />
			</div>
			<div className="border-l border-[var(--glass-border)] p-4">
				<BulletList title="Weaknesses" items={weaknesses} />
			</div>
		</div>
	);
}

function LogoImage({
	src,
	alt,
	fallbackLetter,
	fallbackHue,
}: {
	src: string;
	alt: string;
	fallbackLetter: string;
	fallbackHue: number;
}) {
	const [isLight, setIsLight] = useState(false);
	const [failed, setFailed] = useState(false);
	const { url: accessSrc, status: accessStatus } =
		useBlobAccessUrlWithStatus(src);

	// The visible <img> below deliberately renders without crossOrigin so it
	// always loads (a CORS request that the logo host doesn't allow would leave
	// a blank square). The dark-backdrop cosmetic still needs pixel data, which
	// requires a CORS-clean image, so we sample brightness from a separate,
	// hidden Image() whose failure is harmless — it just skips the backdrop.
	// `displaySrc` is null while an Azure SAS resolves; pointing the visible img
	// at the raw unsigned blob URL in that window would 403 and pin the letter
	// fallback, so we wait for the signed URL instead.
	const displaySrc = accessSrc;

	useEffect(() => {
		// A new source (SAS resolves, or the logo_url is swapped) gets a fresh
		// chance: clear a prior load failure and any stale light-background flag
		// before re-detecting.
		setFailed(false);
		setIsLight(false);
		if (!displaySrc) return;
		let cancelled = false;
		const probe = new Image();
		probe.crossOrigin = 'anonymous';
		probe.onload = () => {
			if (cancelled) return;
			try {
				const canvas = document.createElement('canvas');
				const size = 50;
				canvas.width = size;
				canvas.height = size;
				const ctx = canvas.getContext('2d');
				if (!ctx) return;
				ctx.drawImage(probe, 0, 0, size, size);
				const { data } = ctx.getImageData(0, 0, size, size);
				let total = 0;
				let count = 0;
				for (let i = 0; i < data.length; i += 4) {
					if (data[i + 3] < 10) continue;
					total +=
						(0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2]) / 255;
					count++;
				}
				if (count > 0 && total / count > 0.85) setIsLight(true);
			} catch {
				// cross-origin or other canvas errors — leave default styling
			}
		};
		probe.src = displaySrc;
		return () => {
			cancelled = true;
		};
	}, [displaySrc]);

	// Show the letter chip on a real load failure, and also when the SAS fetch
	// itself errored: that leaves `displaySrc` null just like an in-flight fetch,
	// so the <img> would never get a src and its onError would never fire —
	// without this the card would show a permanent blank square.
	if (failed || accessStatus === 'error') {
		return (
			<div
				className="flex h-[61px] w-[61px] shrink-0 items-center justify-center
					rounded-xl text-lg font-bold text-white"
				style={{ backgroundColor: `hsl(${fallbackHue}, 55%, 50%)` }}
			>
				{fallbackLetter}
			</div>
		);
	}

	return (
		<div
			className={cn(
				'flex h-[61px] w-[61px] shrink-0 items-center justify-center rounded-xl',
				isLight && 'bg-[#333] p-1.5',
			)}
		>
			<img
				src={displaySrc ?? undefined}
				alt={alt}
				onError={() => setFailed(true)}
				className={cn(
					'max-h-full max-w-full object-contain',
					isLight ? 'rounded-lg' : 'rounded-xl',
				)}
			/>
		</div>
	);
}

export const CompetitorCard = memo(function CompetitorCard({
	competitor,
	isExpanded,
	onToggle,
	onRunInsights,
	insightsDisabled,
	isRunningInsights,
	showInsightsButton,
	onUpdateCompetitor,
	onUpdateCompetitorArrayField,
	onUpdateCompetitorSocialUrl,
	onUpdateCompetitorProducts,
	onRemoveCompetitor,
	initialEditing,
	onClearNewCompetitor,
	embedded,
	onEditingChange,
	thinResult,
}: {
	competitor: Competitor;
	isExpanded: boolean;
	onToggle: () => void;
	onRunInsights?: () => void;
	insightsDisabled?: boolean;
	isRunningInsights?: boolean;
	showInsightsButton?: boolean;
	onUpdateCompetitor: (
		id: string,
		field: EditableCompetitorField,
		value: string,
	) => void;
	onUpdateCompetitorArrayField: (
		id: string,
		field: EditableCompetitorArrayField,
		value: string[],
	) => void;
	onUpdateCompetitorSocialUrl: (
		id: string,
		platform: SocialPlatformKey,
		url: string,
	) => void;
	onUpdateCompetitorProducts: (
		id: string,
		products: CompetitorProduct[],
	) => void;
	onRemoveCompetitor: (id: string) => void;
	initialEditing: boolean;
	onClearNewCompetitor: () => void;
	embedded?: boolean;
	onEditingChange?: (
		editing: boolean,
		actions?: { save: () => void; cancel: () => void; canSave: boolean },
	) => void;
	thinResult?: boolean;
}) {
	const hue = hashHue(competitor.name || 'N');

	const [isEditing, setIsEditing] = useState(initialEditing);
	const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);
	const [draftName, setDraftName] = useState('');
	const [draftDescription, setDraftDescription] = useState('');
	const [draftAddress, setDraftAddress] = useState('');
	const [draftPhone, setDraftPhone] = useState('');
	const [draftEmail, setDraftEmail] = useState('');
	const [draftWebsiteUrl, setDraftWebsiteUrl] = useState('');
	const [draftHoursOfOperation, setDraftHoursOfOperation] = useState('');
	const [draftFoundedYear, setDraftFoundedYear] = useState('');
	const [draftEmployeeCount, setDraftEmployeeCount] = useState('');
	const [draftNotes, setDraftNotes] = useState('');
	const [draftInstagram, setDraftInstagram] = useState('');
	const [draftFacebook, setDraftFacebook] = useState('');
	const [draftX, setDraftX] = useState('');
	const [draftLinkedin, setDraftLinkedin] = useState('');
	const [draftTiktok, setDraftTiktok] = useState('');
	const [draftYoutube, setDraftYoutube] = useState('');
	const [draftReddit, setDraftReddit] = useState('');
	const [draftStrengths, setDraftStrengths] = useState<string[]>([]);
	const [draftWeaknesses, setDraftWeaknesses] = useState<string[]>([]);
	const [draftDifferentiators, setDraftDifferentiators] = useState<string[]>(
		[],
	);
	const [draftProducts, setDraftProducts] = useState<CompetitorProduct[]>([]);
	const [draftPrices, setDraftPrices] = useState<Record<string, string>>({});

	const strengthsTags = useEditableTagList({
		serverValue: draftStrengths,
		onSave: (values) => setDraftStrengths(values),
	});
	const weaknessesTags = useEditableTagList({
		serverValue: draftWeaknesses,
		onSave: (values) => setDraftWeaknesses(values),
	});
	const differentiatorsTags = useEditableTagList({
		serverValue: draftDifferentiators,
		onSave: (values) => setDraftDifferentiators(values),
	});

	const cardRef = useRef<HTMLDivElement>(null);
	const competitorSnapshotRef = useRef<Competitor>(competitor);
	const saveRef = useRef<() => void>(() => {});
	const cancelRef = useRef<() => void>(() => {});

	useEffect(() => {
		if (isEditing) {
			cardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const handleEdit = useCallback(() => {
		if (isRunningInsights) return;
		competitorSnapshotRef.current = competitor;
		setDraftName(competitor.name ?? '');
		setDraftDescription(competitor.description ?? '');
		setDraftAddress(competitor.address ?? '');
		setDraftPhone(competitor.phone ?? '');
		setDraftEmail(competitor.email ?? '');
		setDraftWebsiteUrl(competitor.website_url ?? '');
		setDraftHoursOfOperation(competitor.hours_of_operation ?? '');
		setDraftFoundedYear(competitor.founded_year ?? '');
		setDraftEmployeeCount(competitor.employee_count ?? '');
		setDraftNotes(competitor.notes ?? '');
		const sm = competitor.social_media;
		setDraftInstagram(sm?.instagram?.url ?? '');
		setDraftFacebook(sm?.facebook?.url ?? '');
		setDraftX(sm?.x?.url ?? '');
		setDraftLinkedin(sm?.linkedin?.url ?? '');
		setDraftTiktok(sm?.tiktok?.url ?? '');
		setDraftYoutube(sm?.youtube?.url ?? '');
		setDraftReddit(sm?.reddit?.url ?? '');
		setDraftStrengths(
			Array.isArray(competitor.strengths) ? competitor.strengths : [],
		);
		setDraftWeaknesses(
			Array.isArray(competitor.weaknesses) ? competitor.weaknesses : [],
		);
		setDraftDifferentiators(
			Array.isArray(competitor.differentiators)
				? competitor.differentiators
				: [],
		);
		const productList = Array.isArray(competitor.products)
			? competitor.products
			: [];
		setDraftProducts(productList);
		const prices: Record<string, string> = {};
		for (const p of productList) {
			prices[p.id] =
				p.price_usd_cents != null && !isNaN(Number(p.price_usd_cents))
					? (Number(p.price_usd_cents) / 100).toFixed(2)
					: '';
		}
		setDraftPrices(prices);
		setIsEditing(true);
	}, [competitor, isRunningInsights]);

	const handleSave = useCallback(() => {
		const snapshot = competitorSnapshotRef.current;
		const fields: Array<[EditableCompetitorField, string, string | undefined]> =
			[
				['name', draftName, snapshot.name],
				['description', draftDescription, snapshot.description],
				['address', draftAddress, snapshot.address],
				['phone', draftPhone, snapshot.phone],
				['email', draftEmail, snapshot.email],
				['website_url', draftWebsiteUrl, snapshot.website_url],
				[
					'hours_of_operation',
					draftHoursOfOperation,
					snapshot.hours_of_operation,
				],
				['founded_year', draftFoundedYear, snapshot.founded_year],
				['employee_count', draftEmployeeCount, snapshot.employee_count],
				['notes', draftNotes, snapshot.notes],
			];
		for (const [field, draft, current] of fields) {
			if (draft !== (current ?? '')) {
				onUpdateCompetitor(competitor.id, field, draft);
			}
		}
		const sm = snapshot.social_media;
		const socialFields: Array<[SocialPlatformKey, string, string | undefined]> =
			[
				['instagram', draftInstagram, sm?.instagram?.url],
				['facebook', draftFacebook, sm?.facebook?.url],
				['x', draftX, sm?.x?.url],
				['linkedin', draftLinkedin, sm?.linkedin?.url],
				['tiktok', draftTiktok, sm?.tiktok?.url],
				['youtube', draftYoutube, sm?.youtube?.url],
				['reddit', draftReddit, sm?.reddit?.url],
			];
		for (const [platform, draft, current] of socialFields) {
			if (draft !== (current ?? '')) {
				onUpdateCompetitorSocialUrl(competitor.id, platform, draft);
			}
		}
		const finalizeTags = (tags: {
			draftTags: string[];
			newValue: string;
		}): string[] => {
			const out = [...tags.draftTags];
			const trimmed = tags.newValue.trim();
			if (trimmed && !out.includes(trimmed)) out.push(trimmed);
			return out;
		};
		const arrayFields: Array<
			[EditableCompetitorArrayField, string[], string[] | undefined]
		> = [
			['strengths', finalizeTags(strengthsTags), snapshot.strengths],
			['weaknesses', finalizeTags(weaknessesTags), snapshot.weaknesses],
			[
				'differentiators',
				finalizeTags(differentiatorsTags),
				snapshot.differentiators,
			],
		];
		for (const [field, draft, current] of arrayFields) {
			if (JSON.stringify(draft) !== JSON.stringify(current ?? [])) {
				onUpdateCompetitorArrayField(competitor.id, field, draft);
			}
		}
		const productsWithPrices = draftProducts.map((p) => {
			const priceStr = draftPrices[p.id] ?? '';
			const cents = priceStr.trim()
				? Math.round(parseFloat(priceStr) * 100)
				: undefined;
			return {
				...p,
				price_usd_cents: cents != null && !isNaN(cents) ? cents : undefined,
			};
		});
		const currentProducts = snapshot.products ?? [];
		if (
			JSON.stringify(productsWithPrices) !== JSON.stringify(currentProducts)
		) {
			onUpdateCompetitorProducts(competitor.id, productsWithPrices);
		}
		setIsEditing(false);
		onClearNewCompetitor();
	}, [
		competitor,
		draftName,
		draftDescription,
		draftAddress,
		draftPhone,
		draftEmail,
		draftWebsiteUrl,
		draftHoursOfOperation,
		draftFoundedYear,
		draftEmployeeCount,
		draftNotes,
		draftInstagram,
		draftFacebook,
		draftX,
		draftLinkedin,
		draftTiktok,
		draftYoutube,
		draftReddit,
		strengthsTags,
		weaknessesTags,
		differentiatorsTags,
		draftProducts,
		draftPrices,
		onUpdateCompetitor,
		onUpdateCompetitorSocialUrl,
		onUpdateCompetitorArrayField,
		onUpdateCompetitorProducts,
		onClearNewCompetitor,
	]);

	const handleCancel = useCallback(() => {
		if (initialEditing && !draftName.trim()) {
			onRemoveCompetitor(competitor.id);
		}
		setIsEditing(false);
		onClearNewCompetitor();
	}, [
		initialEditing,
		draftName,
		competitor.id,
		onRemoveCompetitor,
		onClearNewCompetitor,
	]);

	saveRef.current = handleSave;
	cancelRef.current = handleCancel;
	const canSave = !!(draftName.trim() && draftDescription.trim());

	useEffect(() => {
		if (!onEditingChange) return;
		if (isEditing) {
			onEditingChange(true, {
				save: () => saveRef.current(),
				cancel: () => cancelRef.current(),
				canSave,
			});
		} else {
			onEditingChange(false);
		}
	}, [isEditing, canSave, onEditingChange]);

	const sm = competitor.social_media;
	const socialEntries = sm
		? SOCIAL_PLATFORMS.filter(({ key }) => sm[key]).map(
				({ key, Icon, followerField }) => {
					const platform = sm[key]!;
					return {
						key,
						url: platform.url,
						icon: <Icon className="h-5 w-5" />,
						label: handleFromUrl(platform.url),
						followers: (platform as Record<string, unknown>)[followerField] as
							| number
							| undefined,
					};
				},
			)
		: [];

	const products = Array.isArray(competitor.products)
		? competitor.products
		: [];

	if (isEditing) {
		return (
			<div
				ref={cardRef}
				className={cn(!embedded && 'glass-card-sm', 'p-6 text-left')}
			>
				<Accordion defaultExpandedKeys={['basic']}>
					<Disclosure id="basic">
						<DisclosureTitle>Basic Info</DisclosureTitle>
						<DisclosurePanel>
							<div className="flex flex-col gap-4 py-2">
								<TextField
									label="Name"
									value={draftName}
									onChange={setDraftName}
									isRequired
								/>
								<TextArea
									label="Description"
									value={draftDescription}
									onChange={setDraftDescription}
									isRequired
								/>
								<TextField
									label="Address"
									value={draftAddress}
									onChange={setDraftAddress}
								/>
							</div>
						</DisclosurePanel>
					</Disclosure>
					<Disclosure id="contact">
						<DisclosureTitle>Contact</DisclosureTitle>
						<DisclosurePanel>
							<div className="flex flex-col gap-4 py-2">
								<div className="grid grid-cols-2 gap-4">
									<TextField
										label="Phone"
										value={draftPhone}
										onChange={setDraftPhone}
									/>
									<TextField
										label="Email"
										value={draftEmail}
										onChange={setDraftEmail}
									/>
								</div>
								<TextField
									label="Website URL"
									value={draftWebsiteUrl}
									onChange={setDraftWebsiteUrl}
								/>
								<TextField
									label="Hours of Operation"
									value={draftHoursOfOperation}
									onChange={setDraftHoursOfOperation}
								/>
							</div>
						</DisclosurePanel>
					</Disclosure>
					<Disclosure id="about">
						<DisclosureTitle>About</DisclosureTitle>
						<DisclosurePanel>
							<div className="flex flex-col gap-4 py-2">
								<div className="grid grid-cols-2 gap-4">
									<TextField
										label="Founded Year"
										value={draftFoundedYear}
										onChange={setDraftFoundedYear}
									/>
									<TextField
										label="Employee Count"
										value={draftEmployeeCount}
										onChange={setDraftEmployeeCount}
									/>
								</div>
								<TextArea
									label="Notes"
									value={draftNotes}
									onChange={setDraftNotes}
								/>
							</div>
						</DisclosurePanel>
					</Disclosure>
					<Disclosure id="social">
						<DisclosureTitle>Social Media</DisclosureTitle>
						<DisclosurePanel>
							<div className="grid grid-cols-2 gap-4 py-2">
								<TextField
									label="Instagram URL"
									value={draftInstagram}
									onChange={setDraftInstagram}
								/>
								<TextField
									label="Facebook URL"
									value={draftFacebook}
									onChange={setDraftFacebook}
								/>
								<TextField
									label="X (Twitter) URL"
									value={draftX}
									onChange={setDraftX}
								/>
								<TextField
									label="LinkedIn URL"
									value={draftLinkedin}
									onChange={setDraftLinkedin}
								/>
								<TextField
									label="TikTok URL"
									value={draftTiktok}
									onChange={setDraftTiktok}
								/>
								<TextField
									label="YouTube URL"
									value={draftYoutube}
									onChange={setDraftYoutube}
								/>
								<TextField
									label="Reddit URL"
									value={draftReddit}
									onChange={setDraftReddit}
								/>
							</div>
						</DisclosurePanel>
					</Disclosure>
					<Disclosure id="analysis">
						<DisclosureTitle>Strengths & Weaknesses</DisclosureTitle>
						<DisclosurePanel>
							<div className="flex flex-col gap-6 py-2">
								<div className="flex flex-col gap-2">
									<SectionLabel>Strengths</SectionLabel>
									<EditableTagList
										tags={strengthsTags}
										placeholder="Add a strength"
									/>
								</div>
								<div className="flex flex-col gap-2">
									<SectionLabel>Weaknesses</SectionLabel>
									<EditableTagList
										tags={weaknessesTags}
										placeholder="Add a weakness"
									/>
								</div>
								<div className="flex flex-col gap-2">
									<SectionLabel>Differentiators</SectionLabel>
									<EditableTagList
										tags={differentiatorsTags}
										placeholder="Add a differentiator"
									/>
								</div>
							</div>
						</DisclosurePanel>
					</Disclosure>
					<Disclosure id="products">
						<DisclosureTitle>Products</DisclosureTitle>
						<DisclosurePanel>
							<div className="flex flex-col gap-4 py-2">
								{draftProducts.map((product, index) => (
									<div
										key={product.id}
										className="flex flex-col gap-1 rounded-lg border
											border-gray-200 p-3"
									>
										<div className="flex justify-end">
											<ActionButton
												aria-label={`Remove ${product.name ?? 'product'}`}
												isQuiet
												size="XS"
												onPress={() => {
													setDraftProducts(
														draftProducts.filter((_, i) => i !== index),
													);
												}}
											>
												<Close />
											</ActionButton>
										</div>
										<div className="flex gap-3">
											<div className="min-w-0 flex-1">
												<TextField
													label="Name"
													value={product.name ?? ''}
													onChange={(val) => {
														const updated = [...draftProducts];
														updated[index] = {
															...product,
															name: val,
														};
														setDraftProducts(updated);
													}}
												/>
											</div>
											<div className="min-w-0 flex-1">
												<TextField
													label="Price ($)"
													value={draftPrices[product.id] ?? ''}
													onChange={(val) => {
														setDraftPrices((prev) => ({
															...prev,
															[product.id]: val,
														}));
													}}
												/>
											</div>
										</div>
										<TextArea
											label="Description"
											value={product.description ?? ''}
											onChange={(val) => {
												const updated = [...draftProducts];
												updated[index] = {
													...product,
													description: val,
												};
												setDraftProducts(updated);
											}}
										/>
									</div>
								))}
								<Button
									variant="secondary"
									size="S"
									onPress={() => {
										const id = crypto.randomUUID();
										setDraftProducts([
											...draftProducts,
											{ id, name: '', description: '' },
										]);
										setDraftPrices((prev) => ({ ...prev, [id]: '' }));
									}}
								>
									<Add />
									<Text>Add product</Text>
								</Button>
							</div>
						</DisclosurePanel>
					</Disclosure>
				</Accordion>
				{!embedded && (
					<div className="mt-4 flex justify-end gap-2">
						<Button variant="secondary" size="S" onPress={handleCancel}>
							Cancel
						</Button>
						<Button
							variant="accent"
							size="S"
							onPress={handleSave}
							isDisabled={!draftName.trim() || !draftDescription.trim()}
						>
							Save
						</Button>
					</div>
				)}
			</div>
		);
	}

	if (isConfirmingDelete) {
		return (
			<div className={cn('glass-card-sm p-6', 'text-left')}>
				<div className="flex flex-col items-center justify-center gap-4 py-4">
					<Text>Are you sure you want to delete?</Text>
					<ButtonGroup>
						<Button
							variant="secondary"
							onPress={() => setIsConfirmingDelete(false)}
						>
							Cancel
						</Button>
						<Button
							variant="negative"
							onPress={() => onRemoveCompetitor(competitor.id)}
						>
							Delete
						</Button>
					</ButtonGroup>
				</div>
			</div>
		);
	}

	return (
		<div className={cn(!embedded && 'glass-card-sm', 'p-6 text-left')}>
			<div
				className={cn(
					'group flex items-start gap-4',
					!embedded && 'cursor-pointer',
				)}
				role={embedded ? undefined : 'button'}
				tabIndex={embedded ? undefined : 0}
				aria-expanded={embedded ? undefined : isExpanded}
				onClick={embedded ? undefined : onToggle}
				onKeyDown={
					embedded
						? undefined
						: (e) => {
								if (e.key === 'Enter' || e.key === ' ') onToggle();
							}
				}
			>
				{competitor.logo_url ? (
					<LogoImage
						src={competitor.logo_url}
						alt={competitor.name}
						fallbackLetter={(competitor.name || 'N').charAt(0)}
						fallbackHue={hue}
					/>
				) : (
					<div
						className="flex h-[61px] w-[61px] shrink-0 items-center
							justify-center rounded-xl text-lg font-bold text-white"
						style={{ backgroundColor: `hsl(${hue}, 55%, 50%)` }}
					>
						{(competitor.name || 'N').charAt(0)}
					</div>
				)}

				<div className="flex min-w-0 flex-1 flex-col gap-1">
					<Text
						styles={style({
							font: 'body',
							fontSize: 'body-lg',
							fontWeight: 'bold',
							color: 'title',
						})}
					>
						{competitor.name}
					</Text>

					<div className="flex items-center gap-1.5 text-sm text-[#666]">
						<Location styles={iconStyle({ color: 'neutral' })} />
						<span>{asDisplayString(competitor.address) ?? 'Web only'}</span>
					</div>

					{asDisplayString(competitor.description) && (
						<div className={cn('mt-1', !isExpanded && 'line-clamp-2')}>
							<Text
								styles={style({
									font: 'body',
									fontSize: 'body-sm',
									color: 'body',
								})}
							>
								{asDisplayString(competitor.description)}
							</Text>
						</div>
					)}

					{thinResult && (
						<InfoBanner className="mt-2">
							<Text
								styles={style({
									font: 'body',
									fontSize: 'body-sm',
									color: 'body',
								})}
							>
								Couldn&apos;t find much on this one — add details manually
							</Text>
						</InfoBanner>
					)}

					{!isExpanded &&
						(socialEntries.length > 0 || competitor.website_url) && (
							<div className="mt-2 flex items-center gap-2">
								{socialEntries.slice(0, 3).map((s) => (
									<a
										key={s.key}
										href={s.url}
										target="_blank"
										rel="noopener noreferrer"
										onClick={(e) => e.stopPropagation()}
										className="text-blue-600"
									>
										{s.icon}
									</a>
								))}
								{asDisplayString(competitor.website_url) && (
									<a
										href={asDisplayString(competitor.website_url) ?? undefined}
										target="_blank"
										rel="noopener noreferrer"
										onClick={(e) => e.stopPropagation()}
										className="text-blue-600"
									>
										<GlobeGrid styles={iconStyle({ color: 'blue' })} />
									</a>
								)}
							</div>
						)}
				</div>

				<div className="flex shrink-0 items-center gap-1">
					{showInsightsButton && (
						<div
							className={cn(
								'shrink-0 transition-opacity',
								isRunningInsights
									? 'opacity-100'
									: 'opacity-0 group-hover:opacity-100',
							)}
							onClick={(e) => e.stopPropagation()}
							onKeyDown={(e) => e.stopPropagation()}
						>
							{isRunningInsights ? (
								<div className="flex items-center gap-2">
									<ProgressCircle
										size="S"
										isIndeterminate
										aria-label="Analyzing"
									/>
									<Text
										styles={style({
											font: 'body',
											fontSize: 'body-sm',
											color: 'neutral',
										})}
									>
										Analyzing...
									</Text>
								</div>
							) : (
								<Button
									variant="secondary"
									size="S"
									isDisabled={insightsDisabled}
									onPress={() => onRunInsights?.()}
									aria-label={`Analyze ${competitor.name}'s Instagram`}
								>
									<MagicWand />
									<Text>Analyze</Text>
								</Button>
							)}
						</div>
					)}

					{embedded ? (
						<Button variant="secondary" size="S" onPress={handleEdit}>
							<Text>Edit</Text>
						</Button>
					) : (
						<div
							className={cn(
								'shrink-0 transition-opacity',
								'opacity-0 group-hover:opacity-100',
							)}
							onClick={(e) => e.stopPropagation()}
							onKeyDown={(e) => e.stopPropagation()}
						>
							<MenuTrigger>
								<ActionButton
									aria-label={`Options for ${competitor.name}`}
									isQuiet
									size="S"
								>
									<More />
								</ActionButton>
								<Popover>
									<Menu
										onAction={(key) => {
											if (key === 'edit') {
												handleEdit();
											} else if (key === 'delete') {
												setIsConfirmingDelete(true);
											}
										}}
									>
										<MenuItem id="edit" isDisabled={isRunningInsights}>
											Edit
										</MenuItem>
										<MenuItem id="delete">Delete</MenuItem>
									</Menu>
								</Popover>
							</MenuTrigger>
						</div>
					)}

					{!embedded && (
						<div className="shrink-0 text-[#888]">
							{isExpanded ? <ChevronUp /> : <ChevronDown />}
						</div>
					)}
				</div>
			</div>

			{isExpanded && (
				<div className="mt-5 ml-[77px] flex flex-col gap-8">
					<BulletList
						title="Reviews"
						items={[
							...(competitor.reviews?.yelp?.score != null
								? [
										`Yelp: ${competitor.reviews.yelp.score}${competitor.reviews.yelp.total_reviews != null ? ` (${competitor.reviews.yelp.total_reviews} reviews)` : ''}`,
									]
								: []),
							...(competitor.reviews?.google?.score != null
								? [
										`Google: ${competitor.reviews.google.score}${competitor.reviews.google.total_reviews != null ? ` (${competitor.reviews.google.total_reviews} reviews)` : ''}`,
									]
								: []),
							...(competitor.website_seo?.domain_authority_score != null
								? [
										`SEO & Web: ${competitor.website_seo.domain_authority_score}`,
									]
								: []),
						]}
					/>

					<StrengthsWeaknesses
						strengths={(Array.isArray(competitor.strengths)
							? competitor.strengths
							: []
						).slice(0, 3)}
						weaknesses={(Array.isArray(competitor.weaknesses)
							? competitor.weaknesses
							: []
						).slice(0, 3)}
					/>
					<BulletList
						title="Differentiators"
						items={(Array.isArray(competitor.differentiators)
							? competitor.differentiators
							: []
						).slice(0, 3)}
					/>

					{socialEntries.length > 0 && (
						<div className="flex flex-col gap-2">
							<SectionLabel>Social Media</SectionLabel>
							<div className="flex flex-col gap-1.5">
								{socialEntries.map((s) => (
									<SocialLink
										key={s.key}
										url={s.url}
										icon={s.icon}
										label={s.label}
										followers={s.followers}
									/>
								))}
							</div>
						</div>
					)}

					{products.length > 0 && (
						<div className="flex flex-col gap-2">
							<SectionLabel>Products</SectionLabel>
							<table className="w-full text-sm">
								<thead>
									<tr
										className="border-b border-gray-200 text-left text-xs
											text-gray-500"
									>
										<th className="pr-3 pb-1.5 font-medium">Name</th>
										<th className="pr-3 pb-1.5 font-medium">Description</th>
										<th className="pb-1.5 text-right font-medium">Price</th>
									</tr>
								</thead>
								<tbody>
									{products.map((p) => (
										<tr key={p.id} className="border-b border-gray-100">
											<td className="py-1.5 pr-3 text-gray-900">{p.name}</td>
											<td className="py-1.5 pr-3 text-gray-500">
												{p.description ?? ''}
											</td>
											<td className="py-1.5 text-right text-gray-700">
												{p.price_usd_cents != null && !isNaN(p.price_usd_cents)
													? `$${(p.price_usd_cents / 100).toFixed(2)}`
													: ''}
											</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
					)}

					{(() => {
						const phone = asDisplayString(competitor.phone);
						const email = asDisplayString(competitor.email);
						const websiteUrl = asDisplayString(competitor.website_url);
						const hours = asDisplayString(competitor.hours_of_operation);
						if (!phone && !email && !websiteUrl && !hours) return null;
						return (
							<div className="flex flex-col gap-1.5">
								<SectionLabel>Contact</SectionLabel>
								{phone && (
									<a
										href={`tel:${phone}`}
										className="text-blue-600 hover:underline"
									>
										<span className="text-sm">{phone}</span>
									</a>
								)}
								{email && (
									<a
										href={`mailto:${email}`}
										className="text-blue-600 hover:underline"
									>
										<span className="text-sm">{email}</span>
									</a>
								)}
								{websiteUrl && (
									<a
										href={websiteUrl}
										target="_blank"
										rel="noopener noreferrer"
										className="text-blue-600 hover:underline"
									>
										<span className="text-sm">{websiteUrl}</span>
									</a>
								)}
								{hours && (
									<Text
										styles={style({
											font: 'body',
											fontSize: 'body-sm',
											color: 'body',
										})}
									>
										{hours}
									</Text>
								)}
							</div>
						);
					})()}

					{(() => {
						const foundedYear = asDisplayString(competitor.founded_year);
						const employeeCount = asDisplayString(competitor.employee_count);
						if (!foundedYear && !employeeCount) return null;
						return (
							<div className="flex flex-col gap-1.5">
								<SectionLabel>About</SectionLabel>
								{foundedYear && (
									<Text
										styles={style({
											font: 'body',
											fontSize: 'body-sm',
											color: 'body',
										})}
									>
										Founded: {foundedYear}
									</Text>
								)}
								{employeeCount && (
									<Text
										styles={style({
											font: 'body',
											fontSize: 'body-sm',
											color: 'body',
										})}
									>
										Employees: {employeeCount}
									</Text>
								)}
							</div>
						);
					})()}

					{competitor.notes && (
						<div className="flex flex-col gap-1.5">
							<SectionLabel>Notes</SectionLabel>
							<Text
								styles={style({
									font: 'body',
									fontSize: 'body-sm',
									color: 'body',
								})}
							>
								{competitor.notes}
							</Text>
						</div>
					)}
				</div>
			)}
		</div>
	);
});
